import { useMemo, useState } from 'react';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { createMessagesI18n } from './i18n';
import CardStats from '../../components/CardStats/CardStats';
import CreateCreateMessagesModal from './CreateCreateMessagesModal';
import ConfirmModal from '../../components/ConfirmModal/ConfirmModal';
import ViewCreateMessagesModal from './ViewCreateMessagesModal';
import usePermissionsMessaging from 'permissions/modules/messaging/usePermissionsMessaging';

const MOCK_TEMPLATES = [
  {
    id: 1,
    name: 'Customer Satisfaction Survey',
    channel: 'whatsapp',
    typeLabel: 'WhatsApp',
    creator: 'Ignacio Olea',
    createdAt: '2024-10-15',
    status: 'Activo',
  },
  {
    id: 2,
    name: 'Payment Reminder',
    channel: 'email',
    typeLabel: 'Email',
    creator: 'María Rodríguez',
    createdAt: '2024-10-10',
    status: 'Activo',
  },
  {
    id: 3,
    name: 'Case Update Notification',
    channel: 'sms',
    typeLabel: 'SMS',
    creator: 'Carlos Méndez',
    createdAt: '2024-10-05',
    status: 'Inactivo',
  },
  {
    id: 4,
    name: 'Legal Consultation Invitation',
    channel: 'whatsapp',
    typeLabel: 'WhatsApp',
    creator: 'Ignacio Olea',
    createdAt: '2024-10-01',
    status: 'Activo',
  },
];

const formatDate = (isoDate) => {
  if (!isoDate) return '';
  const [year, month, day] = isoDate.split('-');
  return `${day}/${month}/${year}`;
};

const CreateMessages = ({ DesignSystemComponents = {} }) => {
  const {
    Button,
    Card,
    Input,
    Badge,
    IconButton,
    SvgIcon,
    Table,
    Modal,
    Dropdown,
  } = DesignSystemComponents;

  const { t } = useScopedTranslation(createMessagesI18n);

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortKey, setSortKey] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);

  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);

  const {
    canCreateTemplate,
    canPreviewTemplate,
    canEditTemplate,
    canDeleteTemplate,
  } = usePermissionsMessaging();

  const total = MOCK_TEMPLATES.length;
  const whatsappCount = MOCK_TEMPLATES.filter(
    (t) => t.channel === 'whatsapp'
  ).length;
  const emailCount = MOCK_TEMPLATES.filter((t) => t.channel === 'email').length;
  const smsCount = MOCK_TEMPLATES.filter((t) => t.channel === 'sms').length;

  const filteredTemplates = useMemo(() => {
    if (!search) return MOCK_TEMPLATES;
    const term = search.toLowerCase();
    return MOCK_TEMPLATES.filter((t) => t.name.toLowerCase().includes(term));
  }, [search]);

  const getSortableValue = (template, key) => {
    switch (key) {
      case 'name':
        return template.name;
      case 'typeLabel':
        return template.typeLabel;
      case 'creator':
        return template.creator;
      case 'createdAt':
        return template.createdAt ? new Date(template.createdAt) : null;
      case 'status':
        return template.status;
      default:
        return template[key];
    }
  };

  const sortedTemplates = useMemo(() => {
    if (!sortKey || !sortDirection) return filteredTemplates;
    const directionFactor = sortDirection === 'desc' ? -1 : 1;

    return [...filteredTemplates].sort((a, b) => {
      const aVal = getSortableValue(a, sortKey);
      const bVal = getSortableValue(b, sortKey);

      if (aVal === null || aVal === undefined) return -1 * directionFactor;
      if (bVal === null || bVal === undefined) return 1 * directionFactor;

      if (aVal instanceof Date && bVal instanceof Date) {
        return aVal.getTime() > bVal.getTime()
          ? directionFactor
          : aVal.getTime() < bVal.getTime()
            ? -directionFactor
            : 0;
      }

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return aVal > bVal
          ? directionFactor
          : aVal < bVal
            ? -directionFactor
            : 0;
      }

      return (
        String(aVal).localeCompare(String(bVal), undefined, {
          sensitivity: 'base',
        }) * directionFactor
      );
    });
  }, [filteredTemplates, sortKey, sortDirection]);

  const handleServerSortChange = ({ sortKey: key, direction }) => {
    setSortKey(direction ? key : null);
    setSortDirection(direction ?? null);
  };

  const getStatusDisplay = (status) => {
    if (status === 'Activo') return t('cm_status_active');
    if (status === 'Inactivo') return t('cm_status_inactive');
    return status;
  };

  const renderChannel = (template) => {
    const iconMap = {
      whatsapp: 'whatsapp',
      email: 'send',
      sms: 'phone',
    };

    const iconColorMap = {
      whatsapp: 'text-success',
      email: 'text-primary',
      sms: 'text-warning',
    };

    return (
      <div className='d-flex align-items-center gap-2'>
        {SvgIcon && (
          <SvgIcon
            name={iconMap[template.channel]}
            size='16px'
            className={iconColorMap[template.channel]}
          />
        )}
        <span>{template.typeLabel}</span>
      </div>
    );
  };

  const onCreateTemplate = () => {
    setEditingTemplate(null);
    setShowTemplateModal(true);
  };

  const handleConfirmDelete = () => {
    if (!toDelete) return;
    // TODO: Call API to delete the template
    setIsDeleteOpen(false);
    setToDelete(null);
  };

  return (
    <div>
      {/* HEADER */}
      <div className='mb-4 d-flex justify-content-between'>
        <div>
          <h2 className=' fw-semibold'>{t('cm_pageTitle')}</h2>
          <h4 className=' fw-normal'>
            {t('cm_pageSubtitle')}
          </h4>
        </div>
        <Button
          label={t('cm_newTemplate')}
          size='sm'
          variant='primary'
          iconStart='plusLg'
          onClick={onCreateTemplate}
          disabled={!canCreateTemplate}
        />
      </div>

      {/* Summary cards */}
      <div className='row mb-4'>
        <div className='col-12 col-md-3'>
          <CardStats
            DesignSystemComponents={DesignSystemComponents}
            title={t('cm_statTotal')}
            count={total}
            subtitle={t('cm_statTotalSub')}
            icon='fileText'
          />
        </div>
        <div className='col-12 col-md-3'>
          <CardStats
            DesignSystemComponents={DesignSystemComponents}
            title={t('cm_statWhatsapp')}
            count={whatsappCount}
            subtitle={t('cm_statWhatsappSub')}
            icon='whatsapp'
            fill
          />
        </div>
        <div className='col-12 col-md-3'>
          <CardStats
            DesignSystemComponents={DesignSystemComponents}
            title={t('cm_statEmail')}
            count={emailCount}
            subtitle={t('cm_statEmailSub')}
            icon='send'
          />
        </div>
        <div className='col-12 col-md-3'>
          <CardStats
            DesignSystemComponents={DesignSystemComponents}
            title={t('cm_statSms')}
            count={smsCount}
            subtitle={t('cm_statSmsSub')}
            icon='phone'
          />
        </div>
      </div>

      {/* Table card */}
      <Card specialPadding='p-4'>
        <div className='d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-3 mb-3'>
          <h2 className='h5 mb-0'>{t('cm_tableTitle')}</h2>
          <div className='w-100 w-md-auto' style={{ maxWidth: 260 }}>
            <Input
              icon='search'
              name='searchTemplate'
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('cm_searchPlaceholder')}
              type='text'
              value={search}
            />
          </div>
        </div>
        <div className='table-container table-templates'>
          {Table ? (
            <Table
              columns={[
                {
                  accessor: 'name',
                  label: t('cm_col_name'),
                  sort: true,
                },
                {
                  accessor: 'typeLabel',
                  label: t('cm_col_type'),
                  sort: true,
                  render: (row) => renderChannel(row),
                },
                {
                  accessor: 'creator',
                  label: t('cm_col_creator'),
                  sort: true,
                },
                {
                  accessor: 'createdAt',
                  label: t('cm_col_createdAt'),
                  sort: true,
                  render: (row) => formatDate(row.createdAt),
                },
                {
                  accessor: 'status',
                  label: t('cm_col_status'),
                  sort: true,
                  render: (row) =>
                    Badge ? (
                      <Badge
                        text={getStatusDisplay(row.status)}
                        color={row.status === 'Activo' ? 'success' : 'light'}
                        size='sm'
                      />
                    ) : (
                      <span
                        className={`badge rounded-pill px-3 py-2 ${
                          row.status === 'Activo'
                            ? 'bg-success-subtle text-success'
                            : 'bg-danger-subtle text-danger'
                        }`}
                      >
                        {getStatusDisplay(row.status)}
                      </span>
                    ),
                },
                {
                  label: t('cm_col_actions'),
                  render: (row) => (
                    <div className='d-flex'>
                      {IconButton ? (
                        <>
                          <IconButton
                            ariaLabel={t('cm_aria_view')}
                            icon='eye'
                            size='md'
                            onClick={() => {
                              setPreviewTemplate(row);
                              setShowPreviewModal(true);
                            }}
                            disabled={!canPreviewTemplate}
                          />
                          <IconButton
                            ariaLabel={t('cm_aria_edit')}
                            icon='pencil'
                            size='md'
                            onClick={() => {
                              setEditingTemplate(row);
                              setShowTemplateModal(true);
                            }}
                            iconStroke='var(--primary-500)'
                            disabled={!canEditTemplate}
                          />
                          <IconButton
                            ariaLabel={t('cm_aria_delete')}
                            icon='trash'
                            size='md'
                            onClick={() => {
                              setToDelete(row);
                              setIsDeleteOpen(true);
                            }}
                            iconStroke='var(--error-500)'
                            disabled={!canDeleteTemplate}
                          />
                        </>
                      ) : (
                        <>
                          <button
                            type='button'
                            className='btn btn-link btn-sm text-decoration-none'
                            onClick={() => {
                              setPreviewTemplate(row);
                              setShowPreviewModal(true);
                            }}
                            disabled={!canPreviewTemplate}
                          >
                            {t('cm_btn_view')}
                          </button>
                          <button
                            type='button'
                            className='btn btn-link btn-sm text-decoration-none'
                            onClick={() => {
                              setEditingTemplate(row);
                              setShowTemplateModal(true);
                            }}
                            disabled={!canEditTemplate}
                          >
                            {t('cm_btn_edit')}
                          </button>
                          <button
                            type='button'
                            className='btn btn-link btn-sm text-danger text-decoration-none'
                            onClick={() => {
                              setToDelete(row);
                              setIsDeleteOpen(true);
                            }}
                            disabled={!canDeleteTemplate}
                          >
                            {t('cm_btn_delete')}
                          </button>
                        </>
                      )}
                    </div>
                  ),
                },
              ]}
              data={sortedTemplates}
              paginator
              paginationMode='client'
              page={page}
              onPageChange={setPage}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              totalItems={sortedTemplates.length}
              serverSortKey={sortKey}
              serverSortDirection={sortDirection}
              onServerSortChange={handleServerSortChange}
              paginatorGroup={[
                { label: '10', value: 10 },
                { label: '20', value: 20 },
                { label: '50', value: 50 },
                { label: '100', value: 100 },
              ]}
            />
          ) : (
            <div className='table-responsive'>
              <table className='table align-middle mb-0'>
                <thead className='color-gray-600'>
                  <tr>
                    <th scope='col'>{t('cm_col_name')}</th>
                    <th scope='col'>{t('cm_col_type')}</th>
                    <th scope='col'>{t('cm_col_creator')}</th>
                    <th scope='col'>{t('cm_col_createdAt')}</th>
                    <th scope='col'>{t('cm_col_status')}</th>
                    <th scope='col' className='text-end'>
                      {t('cm_col_actions')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedTemplates.map((template) => (
                    <tr key={template.id}>
                      <td>{template.name}</td>
                      <td>{renderChannel(template)}</td>
                      <td>{template.creator}</td>
                      <td>{formatDate(template.createdAt)}</td>
                      <td>
                        {Badge ? (
                          <Badge
                            text={getStatusDisplay(template.status)}
                            color={
                              template.status === 'Activo' ? 'success' : 'light'
                            }
                            size='sm'
                          />
                        ) : (
                          <span
                            className={`badge rounded-pill px-3 py-2 ${
                              template.status === 'Activo'
                                ? 'bg-success-subtle text-success'
                                : 'bg-danger-subtle text-danger'
                            }`}
                          >
                            {getStatusDisplay(template.status)}
                          </span>
                        )}
                      </td>
                      <td>
                        <div className='d-flex justify-content-end gap-2'>
                          {IconButton ? (
                            <>
                              <IconButton
                                ariaLabel={t('cm_aria_view')}
                                icon='eye'
                                size='md'
                                onClick={() => {
                                  setPreviewTemplate(template);
                                  setShowPreviewModal(true);
                                }}
                              />
                              <IconButton
                                ariaLabel={t('cm_aria_edit')}
                                icon='pencil'
                                size='md'
                                onClick={() => {
                                  setEditingTemplate(template);
                                  setShowTemplateModal(true);
                                }}
                                iconStroke='var(--primary-500)'
                              />
                              <IconButton
                                ariaLabel={t('cm_aria_delete')}
                                icon='trash'
                                size='md'
                                onClick={() => {
                                  setToDelete(template);
                                  setIsDeleteOpen(true);
                                }}
                                iconStroke='var(--error-500)'
                              />
                            </>
                          ) : (
                            <>
                              <button
                                type='button'
                                className='btn btn-link btn-sm text-decoration-none'
                                onClick={() => {
                                  setPreviewTemplate(template);
                                  setShowPreviewModal(true);
                                }}
                              >
                                {t('cm_btn_view')}
                              </button>
                              <button
                                type='button'
                                className='btn btn-link btn-sm text-decoration-none'
                                onClick={() => {
                                  setEditingTemplate(template);
                                  setShowTemplateModal(true);
                                }}
                              >
                                {t('cm_btn_edit')}
                              </button>
                              <button
                                type='button'
                                className='btn btn-link btn-sm text-danger text-decoration-none'
                                onClick={() => {
                                  setToDelete(template);
                                  setIsDeleteOpen(true);
                                }}
                              >
                                {t('cm_btn_delete')}
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>
      <ViewCreateMessagesModal
        Modal={Modal}
        SvgIcon={SvgIcon}
        show={showPreviewModal}
        template={previewTemplate}
        onClose={() => {
          setShowPreviewModal(false);
          setPreviewTemplate(null);
        }}
      />

      <CreateCreateMessagesModal
        Modal={Modal}
        Input={Input}
        Dropdown={Dropdown}
        Button={Button}
        show={showTemplateModal}
        mode={editingTemplate ? 'edit' : 'create'}
        initialData={editingTemplate}
        onClose={() => {
          setShowTemplateModal(false);
          setEditingTemplate(null);
        }}
        onSave={(payload) => {
          setShowTemplateModal(false);
          setEditingTemplate(null);
          // TODO: refresh templates list from API
        }}
        variableGroups={[]}
      />

      <ConfirmModal
        Modal={Modal}
        show={isDeleteOpen}
        title={t('cm_delete_title')}
        message={
          toDelete
            ? t('cm_delete_message', { name: toDelete.name })
            : t('cm_delete_fallback')
        }
        confirmLabel={t('cm_delete_confirm')}
        confirmVariant='danger'
        onConfirm={handleConfirmDelete}
        onClose={() => {
          setIsDeleteOpen(false);
          setToDelete(null);
        }}
      />
    </div>
  );
};

export default CreateMessages;
